/**
 * The background tiles every Leaflet screen draws on.
 *
 * Not `tile.openstreetmap.org`. That server belongs to the OpenStreetMap
 * Foundation and its tile usage policy reserves it for openstreetmap.org
 * itself and for light development traffic; a canvassing app that opens a map
 * on every volunteer's phone is precisely the third-party use it asks people
 * not to make. When the foundation refuses such traffic it answers `403`, and
 * Leaflet has nothing to draw but an empty grid.
 *
 * The IGN Géoplateforme serves the French public reference map instead: open
 * to reuse, no API key, no account, and the cartography a resident actually
 * recognises — street names, building footprints, house numbers at close
 * zoom. That last point matters at a door: a volunteer reads this map to find
 * a building, not to admire it.
 */
import * as L from 'leaflet';

/**
 * WMTS request for the "Plan IGN v2" layer in Web Mercator (`PM`), with
 * Leaflet's `{z}`/`{x}`/`{y}` placeholders left for it to substitute.
 */
const TILE_URL =
  'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0' +
  '&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM' +
  '&FORMAT=image/png&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';

/** Credit the Géoplateforme asks reusers to display on the map itself. */
const ATTRIBUTION = '&copy; <a href="https://www.ign.fr/">IGN</a> — Géoplateforme';

/** Deepest level the layer publishes; past it Leaflet would ask for nothing. */
const MAX_ZOOM = 19;

/**
 * Add the base map to a Leaflet map and return the layer.
 *
 * @param map - The map to draw the tiles on.
 * @returns The tile layer, already added to `map`.
 */
export function addBasemap(map: L.Map): L.TileLayer {
  return L.tileLayer(TILE_URL, { attribution: ATTRIBUTION, maxZoom: MAX_ZOOM }).addTo(map);
}
